import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Lock, Shield, Zap, Check } from 'lucide-react';
import WompiCheckoutButton from '../payment/WompiCheckoutButton';
import { useSalonId } from '../../hooks/useSalonId';
import './PaymentWallModal.css';

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────

interface PaymentWallContextType {
  /** Call this to force the payment wall to appear */
  showPaymentWall: () => void;
  /** Whether the wall is currently blocking the UI */
  isBlocked: boolean;
}

const PaymentWallContext = createContext<PaymentWallContextType | undefined>(undefined);

export const usePaymentWall = (): PaymentWallContextType => {
  const ctx = useContext(PaymentWallContext);
  if (!ctx) throw new Error('usePaymentWall must be used within PaymentWallProvider');
  return ctx;
};

// ─────────────────────────────────────────────────────────────
// Modal UI
// ─────────────────────────────────────────────────────────────

const PLAN_PRICE = 49_900;

const FEATURES = [
  'Empleados Ilimitados',
  'Clientes Ilimitados',
  'Agenda Inteligente',
  'Panel de Permisos',
  'WhatsApp Reminders',
  'Inventario y Bodegas',
  'Soporte Especializado',
];

function formatCOP(amount: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface PaymentWallModalProps {
  userEmail?: string;
  salonName?: string;
}

function PaymentWallModal({ userEmail = '', salonName = 'Mi Salón' }: PaymentWallModalProps) {
  const salonId = useSalonId();

  return (
    <div className="paywall-overlay" role="dialog" aria-modal="true" aria-label="Suscripción requerida">
      <div className="paywall-card">
        {/* Pulsing lock icon */}
        <div className="paywall-icon-ring">
          <Lock size={30} />
        </div>

        {/* Heading */}
        <h2 className="paywall-heading">
          Tu prueba gratuita<br />
          <span>ha expirado</span>
        </h2>

        <p className="paywall-subtitle">
          Activa Cresta Todo Incluido y sigue gestionando tu salón sin interrupciones.
        </p>

        {/* Price */}
        <div className="paywall-price-block">
          <span className="paywall-price-amount">{formatCOP(PLAN_PRICE)}</span>
          <span className="paywall-price-period">COP / mes</span>
        </div>

        {/* Features pills */}
        <div className="paywall-features">
          {FEATURES.map((f) => (
            <span key={f} className="paywall-feature-pill">
              <Check size={12} />
              {f}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="paywall-cta-wrapper">
          <div className="paywall-wompi-wrapper">
            {salonId ? (
              <WompiCheckoutButton
                planId="pro"
                salonId={salonId}
                salonName={salonName}
                userEmail={userEmail}
                buttonText="💳 Activar Suscripción Ahora"
              />
            ) : (
              <button
                onClick={() => window.location.href = '/login'}
              >
                Iniciar Sesión para Pagar
              </button>
            )}
          </div>

          <p className="paywall-contact-link">
            ¿Preguntas? Escríbenos a{' '}
            <a href="mailto:soporte@cr3sta.com">soporte@cr3sta.com</a>
          </p>
        </div>

        {/* Security footer */}
        <div className="paywall-security-badge">
          <Shield size={14} />
          Pago seguro con Wompi · Débito, Crédito, Nequi y PSE
          <Zap size={12} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

interface PaymentWallProviderProps {
  children: ReactNode;
  userEmail?: string;
  salonName?: string;
}

/**
 * PaymentWallProvider
 *
 * Wrap your authenticated app routes with this provider.
 * It listens for 402 errors from any API call (via a global event) and
 * shows a full-screen blocking modal until the user pays.
 *
 * The apiClient emits a custom event "cresta:trial-expired" when it
 * receives a 402 response with code TRIAL_EXPIRED.
 */
export function PaymentWallProvider({ children, userEmail, salonName }: PaymentWallProviderProps) {
  const [isBlocked, setIsBlocked] = useState(false);

  const showPaymentWall = useCallback(() => {
    setIsBlocked(true);
  }, []);

  // Listen for the global event dispatched by apiClient
  useEffect(() => {
    const handler = () => showPaymentWall();
    window.addEventListener('cresta:trial-expired', handler);
    return () => window.removeEventListener('cresta:trial-expired', handler);
  }, [showPaymentWall]);

  return (
    <PaymentWallContext.Provider value={{ showPaymentWall, isBlocked }}>
      {children}
      {isBlocked && (
        <PaymentWallModal userEmail={userEmail} salonName={salonName} />
      )}
    </PaymentWallContext.Provider>
  );
}

export default PaymentWallProvider;
